'use client';

import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';

import { SWIPE_THRESHOLD_PX } from '@/constants/gesture';

export type SwipeAxis = 'horizontal' | 'vertical';

export type SwipeDirection = 'left' | 'right' | 'up' | 'down';

export type UseSwipeNavigationOptions = {
  /** When `false`, pointer handlers no-op. Defaults to `true`. */
  enabled?: boolean;
  /** Minimum travel in px before a gesture counts as a swipe. */
  threshold?: number;
  /** Axis to recognise. Defaults to `'horizontal'`. */
  axis?: SwipeAxis;
  /**
   * When `true` (default), maps horizontal swipes to `onPrevious` / `onNext` with
   * RTL inversion from `document.documentElement.dir`.
   * LTR: swipe left → next, swipe right → previous.
   * RTL: swipe left → previous, swipe right → next.
   */
  respectRtl?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  /** Fires with the physical swipe direction after a successful gesture. */
  onSwipe?: (direction: SwipeDirection) => void;
};

export type SwipeNavigationHandlers = {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: () => void;
};

type SwipeStart = {
  x: number;
  y: number;
};

type SwipeOptions = {
  enabled: boolean;
  threshold: number;
  axis: SwipeAxis;
  respectRtl: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onSwipe?: (direction: SwipeDirection) => void;
};

function isRtlDocument(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
}

function resolveHorizontalDirection(dx: number): 'left' | 'right' {
  return dx < 0 ? 'left' : 'right';
}

function resolveVerticalDirection(dy: number): 'up' | 'down' {
  return dy < 0 ? 'up' : 'down';
}

/**
 * Returns pointer handlers that detect axis-aligned swipes. Spread onto the
 * swipe surface:
 *
 * ```tsx
 * const swipeHandlers = useSwipeNavigation({
 *   enabled: open,
 *   onPrevious,
 *   onNext,
 * });
 *
 * <div {...swipeHandlers} style={{ touchAction: 'pan-y' }} />
 * ```
 *
 * Mouse pointers are ignored (touch and pen still swipe). Prefer
 * `touch-action: pan-y` (horizontal) or `pan-x` (vertical) on the target so the
 * browser keeps the orthogonal scroll axis native.
 */
export function useSwipeNavigation({
  enabled = true,
  threshold = SWIPE_THRESHOLD_PX,
  axis = 'horizontal',
  respectRtl = true,
  onPrevious,
  onNext,
  onSwipe,
}: UseSwipeNavigationOptions): SwipeNavigationHandlers {
  const startRef = useRef<SwipeStart | null>(null);
  const optionsRef = useRef<SwipeOptions>({
    enabled,
    threshold,
    axis,
    respectRtl,
    onPrevious,
    onNext,
    onSwipe,
  });

  useEffect(() => {
    optionsRef.current = {
      enabled,
      threshold,
      axis,
      respectRtl,
      onPrevious,
      onNext,
      onSwipe,
    };
  }, [enabled, threshold, axis, respectRtl, onPrevious, onNext, onSwipe]);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!optionsRef.current.enabled) return;
    // Mouse drag must not count as a swipe; touch and pen are intentional.
    if (event.pointerType === 'mouse') return;
    startRef.current = { x: event.clientX, y: event.clientY };
  }, []);

  const onPointerCancel = useCallback(() => {
    startRef.current = null;
  }, []);

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const start = startRef.current;
    startRef.current = null;
    if (!start) return;

    const {
      enabled: isEnabled,
      threshold: minDistance,
      axis: swipeAxis,
      respectRtl: useRtl,
      onPrevious: goPrevious,
      onNext: goNext,
      onSwipe: handleSwipe,
    } = optionsRef.current;

    if (!isEnabled) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    if (swipeAxis === 'horizontal') {
      if (absDx < minDistance || absDx <= absDy) return;

      const direction = resolveHorizontalDirection(dx);
      handleSwipe?.(direction);

      const swipedLeft = direction === 'left';
      const goBack = useRtl ? (isRtlDocument() ? swipedLeft : !swipedLeft) : !swipedLeft;
      if (goBack) {
        goPrevious?.();
      } else {
        goNext?.();
      }
      return;
    }

    if (absDy < minDistance || absDy <= absDx) return;

    const direction = resolveVerticalDirection(dy);
    handleSwipe?.(direction);

    // Vertical: swipe up → next, swipe down → previous (no RTL remap).
    if (direction === 'up') {
      goNext?.();
    } else {
      goPrevious?.();
    }
  }, []);

  return { onPointerDown, onPointerUp, onPointerCancel };
}
